import type { Form, FormAccessMode } from '@pm/schemas';
import {
  Badge,
  Button,
  Counter,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Textarea,
} from '@pm/ui';
import { Copy, ExternalLink, FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { fmtDate } from '@/lib/format';
import { formKeys, useClients, useForms, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import {
  ACCESS_MODE_HINT,
  ACCESS_MODE_LABEL,
  AccessModeBadge,
  copyText,
  publicFormUrl,
} from './form-bits';

/** SPEC §31 — formulários externos da organização. */
export function FormsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const { data = [], isLoading, isError, error, refetch } = useForms();
  const [create, setCreate] = useState(false);
  const canManage = can('forms.manage');

  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        title="Formulários"
        subtitle="Entrada de solicitações de clientes. Tudo passa pela Triagem antes de virar demanda; o cliente nunca define prioridade."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setCreate(true)}>
              <Plus /> Novo formulário
            </Button>
          ) : null
        }
      />
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : data.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Nenhum formulário"
            description="Crie um formulário, defina os campos e compartilhe o link com o cliente."
            action={
              canManage ? (
                <Button variant="primary" size="sm" onClick={() => setCreate(true)}>
                  <Plus /> Novo formulário
                </Button>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Formulário</th>
                <th className="h-8 px-3 text-left font-medium">Acesso</th>
                <th className="h-8 px-3 text-left font-medium">Cliente</th>
                <th className="h-8 px-3 text-right font-medium">Campos</th>
                <th className="h-8 px-3 text-right font-medium">Pendentes</th>
                <th className="h-8 px-3 text-left font-medium">Criado</th>
                <th className="h-8 px-3 text-right font-medium">Link</th>
              </tr>
            </thead>
            <tbody>
              {data.map((f) => (
                <tr
                  key={f.id}
                  className="h-row cursor-pointer border-b border-border last:border-b-0 hover:bg-surface-2"
                  onClick={() => navigate(`/formularios/${f.id}`)}
                >
                  <td className="px-3">
                    <Link to={`/formularios/${f.id}`} className="font-medium hover:text-accent">
                      {f.name}
                    </Link>
                    {f.status !== 'ACTIVE' ? (
                      <Badge tone="neutral" className="ml-2">
                        {f.status === 'INACTIVE' ? 'Inativo' : 'Arquivado'}
                      </Badge>
                    ) : null}
                  </td>
                  <td className="px-3">
                    <AccessModeBadge mode={f.access_mode} />
                  </td>
                  <td className="px-3 text-sm text-text-2">
                    {f.client?.name ?? <span className="text-text-3">—</span>}
                  </td>
                  <td className="px-3 text-right font-mono text-sm">{f.fields.length}</td>
                  <td className="px-3 text-right">
                    {f.pending_count > 0 ? (
                      <Counter tone="accent">{f.pending_count}</Counter>
                    ) : (
                      <span className="font-mono text-xs text-text-3">0/{f.requests_count}</span>
                    )}
                  </td>
                  <td className="px-3 font-mono text-xs text-text-3">{fmtDate(f.created_at)}</td>
                  <td className="px-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {f.access_mode !== 'AUTHENTICATED_CLIENT' ? (
                      <span className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Copiar link"
                          onClick={() =>
                            void copyText(publicFormUrl(f.slug)).then((ok) =>
                              ok
                                ? toast.success('Link copiado')
                                : toast.error('Não foi possível copiar'),
                            )
                          }
                        >
                          <Copy />
                        </Button>
                        <a
                          className="inline-flex size-7 items-center justify-center rounded text-text-2 hover:bg-surface-2 hover:text-text-1"
                          href={publicFormUrl(f.slug)}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Abrir"
                        >
                          <ExternalLink className="size-4" />
                        </a>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent title="Novo formulário" size="sm">
          <CreateFormForm onDone={() => setCreate(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateFormForm({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const agency = me.organization?.mode === 'AGENCY';
  const { data: clients } = useClients({ status: 'ACTIVE' });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<FormAccessMode>('PUBLIC_URL');
  const [clientId, setClientId] = useState('');
  const save = useInvalidating(
    (input: Record<string, unknown>) => api.post<Form>('/forms', input),
    [formKeys.forms],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          {
            name,
            description: description || null,
            access_mode: mode,
            client_id: clientId || null,
            fields: [
              { field_type: 'TEXT', label: 'Assunto', name: 'assunto', required: true },
              {
                field_type: 'TEXTAREA',
                label: 'Descreva sua solicitação',
                name: 'descricao',
                required: true,
              },
            ],
          },
          {
            onSuccess: (f) => (
              toast.success('Formulário criado'),
              onDone(),
              navigate(`/formularios/${f.id}`)
            ),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <Field label="Nome" htmlFor="f-name">
        <Input
          id="f-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex.: Briefing de campanha"
        />
      </Field>
      <Field label="Descrição (aparece para o cliente)" htmlFor="f-desc">
        <Textarea
          id="f-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="min-h-[64px]"
        />
      </Field>
      <Field label="Acesso" htmlFor="f-mode" hint={ACCESS_MODE_HINT[mode]}>
        <Select
          id="f-mode"
          value={mode}
          onChange={(e) => setMode(e.target.value as FormAccessMode)}
        >
          {(Object.keys(ACCESS_MODE_LABEL) as FormAccessMode[]).map((m) => (
            <option key={m} value={m}>
              {ACCESS_MODE_LABEL[m]}
            </option>
          ))}
        </Select>
      </Field>
      {agency ? (
        <Field
          label="Cliente"
          htmlFor="f-client"
          hint="Solicitações deste formulário pertencem a este cliente."
        >
          <Select id="f-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Sem cliente fixo (definir na triagem)</option>
            {(clients?.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.trade_name ?? c.legal_name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={save.isPending}
          disabled={name.trim().length < 2}
        >
          Criar
        </Button>
      </DialogFooter>
    </form>
  );
}
